import React, { useMemo, useState } from 'react';
import { Repeat, Plus, Trash2, Edit, Play, Power, X, Mail, Calendar, Loader2 } from 'lucide-react';
import { useData } from '../context/DataContext';
import { carsysApi } from '../api/carsysApi';
import { confirmDialog } from '../lib/confirm';
import { toast } from 'sonner';
import { useT } from '../context/LanguageContext';

const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });

const FREQ = [
  { v: 'maandelijks', l: 'Maandelijks' },
  { v: 'per_kwartaal', l: 'Per kwartaal' },
  { v: 'halfjaarlijks', l: 'Halfjaarlijks' },
  { v: 'jaarlijks', l: 'Jaarlijks' },
];
const freqLabel = (v) => (FREQ.find(f => f.v === v) || {}).l || v;

const leegRegel = () => ({ omschrijving: '', aantal: 1, prijs: 0 });
const leegFormulier = () => ({
  klantId: '', kenteken: '', omschrijving: '',
  regels: [leegRegel()], kortingType: 'vast', kortingWaarde: 0,
  frequentie: 'maandelijks', startDatum: new Date().toISOString().slice(0, 10),
  eindDatum: '', autoVerstuur: false, actief: true,
});

export default function Herhaalfacturen() {
  const { t } = useT();
  const { klanten } = useData();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // form object (create/edit)
  const [busyId, setBusyId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    carsysApi.getHerhaalfacturen().then(setItems).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const klantNaam = (id) => (klanten.find(k => k.id === id) || {}).naam || '—';

  const subtotaal = useMemo(() => {
    if (!modal) return 0;
    let s = 0;
    (modal.regels || []).forEach(r => { s += (Number(r.aantal) || 0) * (Number(r.prijs) || 0); });
    const kw = Number(modal.kortingWaarde) || 0;
    const korting = modal.kortingType === 'percentage' ? s * Math.min(100, kw) / 100 : Math.min(s, kw);
    return Math.max(0, s - korting);
  }, [modal]);

  const openNew = () => setModal(leegFormulier());
  const openEdit = (it) => setModal({ ...it, eindDatum: it.eindDatum || '', regels: it.regels?.length ? it.regels : [leegRegel()] });

  const setF = (upd) => setModal(prev => ({ ...prev, ...upd }));
  const setRegel = (i, upd) => setModal(prev => ({ ...prev, regels: prev.regels.map((r, idx) => idx === i ? { ...r, ...upd } : r) }));
  const addRegel = () => setModal(prev => ({ ...prev, regels: [...prev.regels, leegRegel()] }));
  const delRegel = (i) => setModal(prev => ({ ...prev, regels: prev.regels.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!modal.klantId) { toast.error(t('Kies een klant', 'Kies een klant')); return; }
    const regels = (modal.regels || []).filter(r => (r.omschrijving || '').trim() && (Number(r.prijs) || 0) !== 0);
    if (!regels.length) { toast.error(t('Voeg minstens één regel met bedrag toe', 'Voeg minstens één regel met bedrag toe')); return; }
    const payload = {
      klantId: modal.klantId, kenteken: modal.kenteken, omschrijving: modal.omschrijving,
      regels, kortingType: modal.kortingType, kortingWaarde: Number(modal.kortingWaarde) || 0,
      frequentie: modal.frequentie, startDatum: modal.startDatum, eindDatum: modal.eindDatum || null,
      autoVerstuur: !!modal.autoVerstuur, actief: !!modal.actief,
    };
    setSaving(true);
    try {
      if (modal.id) await carsysApi.updateHerhaalfactuur(modal.id, payload);
      else await carsysApi.createHerhaalfactuur(payload);
      toast.success(t('Opgeslagen', 'Opgeslagen'));
      setModal(null);
      load();
    } catch (e) { toast.error(t('Opslaan mislukt', 'Opslaan mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };

  const toggleActief = async (it) => {
    try { await carsysApi.updateHerhaalfactuur(it.id, { actief: !it.actief }); load(); }
    catch (e) { toast.error(t('Wijzigen mislukt', 'Wijzigen mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  const genereerNu = async (it) => {
    if (!(await confirmDialog(`${t('Nu een factuur genereren voor', 'Nu een factuur genereren voor')} ${klantNaam(it.klantId)}?`))) return;
    setBusyId(it.id);
    try {
      const res = await carsysApi.genereerHerhaalfactuurNu(it.id);
      toast.success(t('Factuur gegenereerd', 'Factuur gegenereerd'), { description: `${res.factuur?.nummer} · ${eur(res.factuur?.totaal)}` });
      load();
    } catch (e) { toast.error(t('Genereren mislukt', 'Genereren mislukt'), { description: e.response?.data?.detail || e.message }); }
    finally { setBusyId(null); }
  };

  const verwijder = async (it) => {
    if (!(await confirmDialog(t('Herhaalfactuur verwijderen?', 'Herhaalfactuur verwijderen?')))) return;
    try { await carsysApi.deleteHerhaalfactuur(it.id); toast.success(t('Verwijderd', 'Verwijderd')); load(); }
    catch (e) { toast.error(t('Verwijderen mislukt', 'Verwijderen mislukt'), { description: e.response?.data?.detail || e.message }); }
  };

  return (
    <div data-testid="herhaalfacturen-page">
      <div className="cs-page-header">
        <div><h2>{t('Herhaalfacturen', 'Herhaalfacturen')}</h2><p>{t('Genereer periodiek automatisch facturen voor terugkerende klanten', 'Genereer periodiek automatisch facturen voor terugkerende klanten')}</p></div>
        <button className="cs-btn primary" data-testid="btn-add-herhaalfactuur" onClick={openNew}><Plus size={14} /> {t('Nieuwe herhaalfactuur', 'Nieuwe herhaalfactuur')}</button>
      </div>

      <div className="cs-card">
        <table className="cs-table">
          <thead><tr>
            <th>{t('Klant', 'Klant')}</th>
            <th>{t('Frequentie', 'Frequentie')}</th>
            <th>{t('Volgende factuur', 'Volgende factuur')}</th>
            <th>{t('Einddatum', 'Einddatum')}</th>
            <th style={{ textAlign: 'center' }}>{t('Auto e-mail', 'Auto e-mail')}</th>
            <th style={{ textAlign: 'center' }}>{t('Aantal', 'Aantal')}</th>
            <th style={{ textAlign: 'center' }}>{t('Status', 'Status')}</th>
            <th></th>
          </tr></thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="cs-empty"><Loader2 size={16} className="animate-spin" style={{ verticalAlign: -3, marginRight: 6 }} />{t('Laden…', 'Laden…')}</td></tr>}
            {!loading && items.map(it => (
              <tr key={it.id} data-testid={`herhaal-row-${it.id}`}>
                <td style={{ fontWeight: 600 }}>{klantNaam(it.klantId)}{it.omschrijving ? <span style={{ display: 'block', fontSize: 11, color: 'var(--cs-text-muted)', fontWeight: 400 }}>{it.omschrijving}</span> : null}</td>
                <td>{freqLabel(it.frequentie)}</td>
                <td><Calendar size={12} style={{ verticalAlign: -1, marginRight: 4, color: 'var(--cs-text-muted)' }} />{it.volgendeDatum ? new Date(it.volgendeDatum).toLocaleDateString('nl-NL') : '—'}</td>
                <td>{it.eindDatum ? new Date(it.eindDatum).toLocaleDateString('nl-NL') : <span style={{ color: 'var(--cs-text-muted)' }}>{t('geen', 'geen')}</span>}</td>
                <td style={{ textAlign: 'center' }}>{it.autoVerstuur ? <Mail size={14} style={{ color: 'var(--cs-green)' }} /> : '—'}</td>
                <td style={{ textAlign: 'center' }}>{it.aantalGegenereerd || 0}</td>
                <td style={{ textAlign: 'center' }}>
                  <button className={`cs-badge ${it.actief ? 'green' : 'gray'}`} style={{ cursor: 'pointer', border: 'none' }} data-testid={`herhaal-toggle-${it.id}`} onClick={() => toggleActief(it)} title={t('Klik om aan/uit te zetten', 'Klik om aan/uit te zetten')}>
                    <Power size={11} style={{ verticalAlign: -1 }} /> {it.actief ? t('Actief', 'Actief') : t('Gepauzeerd', 'Gepauzeerd')}
                  </button>
                </td>
                <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button className="cs-btn ghost" style={{ padding: 4 }} title={t('Nu genereren', 'Nu genereren')} data-testid={`herhaal-genereer-${it.id}`} disabled={busyId === it.id} onClick={() => genereerNu(it)}>{busyId === it.id ? <Loader2 size={12} className="animate-spin" /> : <Play size={12} />}</button>
                  <button className="cs-btn ghost" style={{ padding: 4 }} title={t('Bewerken', 'Bewerken')} data-testid={`herhaal-edit-${it.id}`} onClick={() => openEdit(it)}><Edit size={12} /></button>
                  <button className="cs-btn ghost danger" style={{ padding: 4 }} title={t('Verwijderen', 'Verwijderen')} data-testid={`herhaal-del-${it.id}`} onClick={() => verwijder(it)}><Trash2 size={12} /></button>
                </td>
              </tr>
            ))}
            {!loading && items.length === 0 && <tr><td colSpan={8} className="cs-empty">{t('Nog geen herhaalfacturen. Maak er één voor abonnementen, onderhoudscontracten of vaste maandbedragen.', 'Nog geen herhaalfacturen. Maak er één voor abonnementen, onderhoudscontracten of vaste maandbedragen.')}</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="cs-modal-overlay" onClick={() => setModal(null)}>
          <div className="cs-modal" onClick={e => e.stopPropagation()} style={{ width: 640, maxWidth: '94vw' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ marginTop: 0 }}><Repeat size={16} style={{ verticalAlign: -2, marginRight: 6 }} />{modal.id ? t('Herhaalfactuur bewerken', 'Herhaalfactuur bewerken') : t('Nieuwe herhaalfactuur', 'Nieuwe herhaalfactuur')}</h3>
              <button className="cs-btn ghost" onClick={() => setModal(null)} data-testid="herhaal-close"><X size={16} /></button>
            </div>
            <div style={{ display: 'grid', gap: 10 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label className="cs-section-title">{t('Klant', 'Klant')}</label>
                  <select className="cs-input" data-testid="herhaal-klant" value={modal.klantId} onChange={e => setF({ klantId: e.target.value })}>
                    <option value="">{t('— Kies klant —', '— Kies klant —')}</option>
                    {klanten.map(k => <option key={k.id} value={k.id}>{k.naam}</option>)}
                  </select>
                </div>
                <div><label className="cs-section-title">{t('Kenteken', 'Kenteken')} <span style={{ fontSize: 10, color: 'var(--cs-text-muted)' }}>({t('optioneel', 'optioneel')})</span></label>
                  <input className="cs-input" data-testid="herhaal-kenteken" value={modal.kenteken} onChange={e => setF({ kenteken: e.target.value.toUpperCase() })} />
                </div>
              </div>
              <div><label className="cs-section-title">{t('Omschrijving', 'Omschrijving')}</label>
                <input className="cs-input" data-testid="herhaal-omschrijving" placeholder={t('bv. Onderhoudscontract maandelijks', 'bv. Onderhoudscontract maandelijks')} value={modal.omschrijving} onChange={e => setF({ omschrijving: e.target.value })} />
              </div>

              <div>
                <label className="cs-section-title">{t('Factuurregels', 'Factuurregels')}</label>
                {modal.regels.map((r, i) => (
                  <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 70px 110px 32px', gap: 8, marginBottom: 6 }}>
                    <input className="cs-input" placeholder={t('Omschrijving', 'Omschrijving')} value={r.omschrijving} onChange={e => setRegel(i, { omschrijving: e.target.value })} data-testid={`herhaal-regel-oms-${i}`} />
                    <input className="cs-input" type="number" step="0.01" placeholder={t('Aantal', 'Aantal')} value={r.aantal} onChange={e => setRegel(i, { aantal: e.target.value })} data-testid={`herhaal-regel-aantal-${i}`} />
                    <input className="cs-input" type="number" step="0.01" placeholder={t('Prijs (excl.)', 'Prijs (excl.)')} value={r.prijs} onChange={e => setRegel(i, { prijs: e.target.value })} data-testid={`herhaal-regel-prijs-${i}`} />
                    <button className="cs-btn ghost danger" style={{ padding: 4 }} onClick={() => delRegel(i)} disabled={modal.regels.length <= 1}><Trash2 size={13} /></button>
                  </div>
                ))}
                <button className="cs-btn ghost" data-testid="herhaal-add-regel" onClick={addRegel}><Plus size={13} /> {t('Regel toevoegen', 'Regel toevoegen')}</button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label className="cs-section-title">{t('Korting', 'Korting')}</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input className="cs-input" type="number" step="0.01" style={{ flex: 1 }} value={modal.kortingWaarde} onChange={e => setF({ kortingWaarde: e.target.value })} data-testid="herhaal-korting-waarde" />
                    <select className="cs-input" style={{ width: 90 }} value={modal.kortingType} onChange={e => setF({ kortingType: e.target.value })} data-testid="herhaal-korting-type">
                      <option value="vast">€</option><option value="percentage">%</option>
                    </select>
                  </div>
                </div>
                <div><label className="cs-section-title">{t('Frequentie', 'Frequentie')}</label>
                  <select className="cs-input" data-testid="herhaal-frequentie" value={modal.frequentie} onChange={e => setF({ frequentie: e.target.value })}>
                    {FREQ.map(f => <option key={f.v} value={f.v}>{t(f.l, f.l)}</option>)}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div><label className="cs-section-title">{t('Startdatum', 'Startdatum')}</label>
                  <input className="cs-input" type="date" data-testid="herhaal-startdatum" value={(modal.startDatum || '').slice(0, 10)} onChange={e => setF({ startDatum: e.target.value })} />
                </div>
                <div><label className="cs-section-title">{t('Einddatum', 'Einddatum')} <span style={{ fontSize: 10, color: 'var(--cs-text-muted)' }}>({t('optioneel', 'optioneel')})</span></label>
                  <input className="cs-input" type="date" data-testid="herhaal-einddatum" value={(modal.eindDatum || '').slice(0, 10)} onChange={e => setF({ eindDatum: e.target.value })} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={!!modal.autoVerstuur} onChange={e => setF({ autoVerstuur: e.target.checked })} data-testid="herhaal-autoverstuur" />
                  {t('Factuur automatisch naar klant e-mailen', 'Factuur automatisch naar klant e-mailen')}
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={!!modal.actief} onChange={e => setF({ actief: e.target.checked })} data-testid="herhaal-actief" />
                  {t('Actief', 'Actief')}
                </label>
              </div>

              <div style={{ fontSize: 13, padding: '8px 10px', background: 'var(--cs-card-alt)', borderRadius: 6 }} data-testid="herhaal-preview">
                {t('Subtotaal (excl. BTW)', 'Subtotaal (excl. BTW)')}: <b>{eur(subtotaal)}</b> · <span style={{ color: 'var(--cs-text-muted)' }}>{t('BTW volgt je instelling bij het genereren', 'BTW volgt je instelling bij het genereren')}</span>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
              <button className="cs-btn" onClick={() => setModal(null)}>{t('Annuleren', 'Annuleren')}</button>
              <button className="cs-btn primary" data-testid="herhaal-save" onClick={save} disabled={saving}>{saving ? <Loader2 size={14} className="animate-spin" /> : null} {t('Opslaan', 'Opslaan')}</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .cs-modal-overlay { position: fixed; inset: 0; background: rgba(15,23,42,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; }
        .cs-modal { background: #fff; border-radius: 10px; padding: 20px 22px; box-shadow: 0 20px 60px rgba(0,0,0,0.25); max-height: 92vh; overflow-y: auto; }
      `}</style>
    </div>
  );
}
